import { useEffect, useRef, useState } from "react";
import { logDebug, logWarningNoStack } from "../LocalLogger";
import { appStore, BlueprintSimple, printifyRequest } from "../utils/AppMobx";
import { IConceptBlueprint, IConceptDesign, IVariantSimple } from "./ConceptsPage";
import { PreviewImage } from "../UI/ClickableImage";
import { ProductType } from "./productTypes";
import { on } from "events";

// Interface for the pattern object inside images
interface IPrintAreaImagePattern {
	spacing_x: number;
	spacing_y: number;
	scale: number;
	offset: number;
}

// Interface for each image in placeholders
interface IPrintAreaImage {
	id: string;
	x: number;
	y: number;
	scale: number;
	angle: number;
	pattern?: IPrintAreaImagePattern;
}

// Interface for each placeholder in print_areas
interface IPrintAreaPlaceholder {
	position: string;
	images: IPrintAreaImage[];
}

// Interface for each print_area
interface IPrintArea {
	variant_ids: number[];
	placeholders: IPrintAreaPlaceholder[];
}

// Interface for each variant
interface IProductVariant {
	id: number;
	price: number;
	is_enabled: boolean;
}

// Main interface for the product
export interface IProductCreation {
	title: string;
	description: string;
	safety_information?: string;
	blueprint_id: number;
	storeCurrentProductId?: string;
	print_provider_id: number;
	variants: IProductVariant[];
	print_areas: IPrintArea[];
}
export const alwaysFilter = ["4XL", "5XL", "6XL", "7XL", "8XL"].map((s) => s.toLowerCase());

export function getVariantForDesign(
	design: IConceptDesign,
	variants: IVariantSimple[] = [],
	colorMap: Record<string, string>,
	conceptBlueprint: IConceptBlueprint
) {
	const retVariant: IVariantSimple[] = [];
	const coloredVariant: IVariantSimple[] = [];
	design.selectedColors?.forEach((color) => {
		const colorName = Object.entries(colorMap).find((e) => e[1].toLowerCase() === color.toLowerCase())?.[0];
		if (!colorName) {
			logWarningNoStack(`Color ${color} not found in colorMap. Available colors:`, colorMap, design.selectedColors);
			// alert(`Color ${color} not found in colorMap. check log for details.`);
			return;
		}
		const variant = variants.filter((v) => v.options.color === colorName);
		if (variant) {
			coloredVariant.push(...variant);
		}
	});

	coloredVariant.forEach((variant) => {
		let filteredOut = false;
		conceptBlueprint.otherFilters?.forEach((filter) => {
			// filter out variants that match the filter
			const optionValue = variant.options[filter.type] as string;
			const filterValue = filter.value as string;
			if (optionValue.toLowerCase() === filterValue.toLowerCase()) {
				filteredOut = true;
			}
			if (alwaysFilter.includes(optionValue.toLowerCase())) {
				filteredOut = true;
			}
		});
		if (!filteredOut) {
			retVariant.push(variant);
		} else {
			// logDebug(`Variant ${variant.id} filtered out by ${JSON.stringify(conceptBlueprint.otherFilters)}`);
		}
	});
	logDebug("this are the variants", retVariant);
	return retVariant;
}

export function createProductCreation(
	conceptBlueprint: IConceptBlueprint,
	printifyBlueprint: BlueprintSimple,
	variants: IVariantSimple[],
	colorMap: Record<string, string>,
	printiryRefProduct: ProductType
) {
	const productCreation: Partial<IProductCreation> = {};

	const designsVariants = conceptBlueprint.designs.map((design) => {
		return {
			design,
			variants: getVariantForDesign(design, variants, colorMap, conceptBlueprint),
		};
	});

	const uniqueVariantIds = new Set<number>();
	designsVariants.forEach((designVariant) =>
		designVariant.variants.forEach((variant) => {
			if (!uniqueVariantIds.has(variant.id)) {
				uniqueVariantIds.add(variant.id);
			} else {
				alert(`Variant ${variant.id} is mutually exclusive and cannot be used in multiple designs.`);
			}
		})
	);
	const price = Object.values(conceptBlueprint.prices || {}).find((p) => p && !isNaN(p) && p > 0);
	if (!price) {
		alert("No valid price found in concept blueprint.");
		return null;
	}
	productCreation.variants = designsVariants
		.map((variantList) =>
			variantList.variants.map((variant) => ({
				id: variant.id,
				price: price * 100,
				is_enabled: true,
			}))
		)
		.flat();
	productCreation.print_areas = designsVariants.map((designsVariant) => {
		const printArea: IPrintArea = {
			variant_ids: [],
			placeholders: designsVariant.design.images.map((image) => ({
				position: image.position,
				images: [
					{
						id: image.id,
						x: 0.5,
						y: 0.5,
						scale: 1,
						angle: 0,
					},
				],
			})),
		};

		printArea.variant_ids = designsVariant.variants.map((variant) => variant.id);
		return printArea;
	});
	productCreation.title = conceptBlueprint.title;
	productCreation.description = conceptBlueprint.description; // "This is a preview product created for the concept blueprint";
	//@ts-ignore
	productCreation.tags = [...(printiryRefProduct.tags || []), ...(conceptBlueprint.tags || [])];
	if (conceptBlueprint.tags && conceptBlueprint.tags.length > 0) {
		productCreation.description =
			`${conceptBlueprint.tags.map((t) => `col:${t}`).join(";")}!~~TAGS~~!<br>` + productCreation.description;
	}
	productCreation.storeCurrentProductId = conceptBlueprint.storedProductId;
	productCreation.blueprint_id = printifyBlueprint.id;
	productCreation.print_provider_id = appStore.selectedProviderId;
	logDebug("productCreation payload", productCreation);
	return productCreation as IProductCreation;
}

export const PreviewPage: React.FC<{
	onClose: () => void;
	printiryRefProduct: ProductType;
	colorMap: Record<string, string>;
	variants: IVariantSimple[];
	conceptBlueprint: IConceptBlueprint;
	printifyBlueprint: BlueprintSimple;
}> = ({ printiryRefProduct, conceptBlueprint, printifyBlueprint, variants, colorMap, onClose }) => {
	const processingDesign = useRef(false);
	const [product, setProduct] = useState<any>(null);
	useEffect(() => {
		if (processingDesign.current) {
			return;
		}
		processingDesign.current = true;

		async function createDummyProductFromPayload(_productCreation: IProductCreation) {
			appStore.showLoading = true;
			try {
				const product = await printifyRequest("createProductFromPayload", {
					payload: _productCreation,
					dummy: true,
				});
				logDebug("Product created successfully:", product);
				setProduct(product);
			} catch (err) {}
			appStore.showLoading = false;
		}
		const productCreation = createProductCreation(conceptBlueprint, printifyBlueprint, variants, colorMap, printiryRefProduct);
		if (!productCreation) {
			alert("Failed to create product creation payload. Please check the console for details.");
			onClose();
			return;
		}
		logDebug("productCreation", productCreation);
		if (productCreation.variants.length > 100) {
			alert(
				`Too many variants (${productCreation.variants.length}) for a single product. Please reduce the number of colors. ( max 100 )`
			);
			onClose();
			return;
		}
		createDummyProductFromPayload(productCreation);
	}, []);

	return (
		<div className="flex flex-col items-center justify-start h-[90vh] w-[60vw] bg-gray-100 overflow-y-auto p-4">
			<h1 className="text-xl font-bold mb-4">Preview Page</h1>
			{product ? (
				<div className="bg-white p-4 rounded shadow w-full">
					<h2 className="text-lg font-semibold mb-2">Product Created Successfully</h2>
					<p className="mb-2">Product ID: {product.id}</p>
					<p className="mb-2">Title: {product.title}</p>
					<p className="mb-2">Description: {product.description}</p>
					<p className="mb-2">Blueprint ID: {product.blueprint_id}</p>
					<p className="mb-2">Print Provider ID: {product.print_provider_id}</p>
					<h3 className="text-md font-semibold mt-4">Images:</h3>
					<div className="grid grid-cols-4 gap-4">
						{product.images.map((image: { id: string; src: string; position: string }, index: number) => {
							if (image.position !== "front") {
								return null;
							}
							return (
								<div key={index} className="flex flex-col items-center">
									<PreviewImage imageUrl={image.src} cls="w-32 h-32 object-cover mb-2" />
									<p className="text-sm text-gray-600">Image ID: {image.id}</p>
								</div>
							);
						})}
					</div>
				</div>
			) : (
				<p>Loading product...</p>
			)}
			{/* <pre className="bg-white p-4 rounded min-h-[30%] shadow text-left w-full  overflow-auto">
				{JSON.stringify(conceptBlueprint, null, 2)}
			</pre>
			<pre className="bg-white p-4 rounded min-h-[30%] shadow text-left w-full  overflow-auto">
				{JSON.stringify(printifyBlueprint, null, 2)}
			</pre>
			<pre className="bg-white p-4 rounded min-h-[30%] shadow text-left w-full  overflow-auto">
				{JSON.stringify(variants, null, 2)}
			</pre>
			<pre className="bg-white p-4 rounded min-h-[30%] shadow text-left w-full  overflow-auto">
				{JSON.stringify(colorMap, null, 2)}
			</pre> */}
		</div>
	);
};
